/**
 * Pure countdown maths for the "registration will be open in" timer.
 *
 * Framework-agnostic so it can be unit tested without a renderer and reused by
 * mobile. The React wiring lives in the platform hook that consumes this.
 */

export type TimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export const ZERO_TIME_LEFT: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
}

/**
 * Breaks the remaining milliseconds until `targetDate` into d/h/m/s.
 *
 * Returns all zeroes once the target has passed (or when the target is not a
 * valid date), so an expired countdown renders 0s rather than freezing on the
 * last positive tick.
 */
export const getTimeLeftUntil = (
  targetDate: string | number | Date | null | undefined,
  now: number
): TimeLeft => {
  if (!targetDate) {
    return ZERO_TIME_LEFT
  }

  const target = new Date(targetDate).getTime()

  if (Number.isNaN(target)) {
    return ZERO_TIME_LEFT
  }

  const difference = target - now

  if (difference <= 0) {
    return ZERO_TIME_LEFT
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  }
}

/**
 * True once the countdown target has been reached.
 *
 * A missing/invalid target counts as elapsed: there is nothing to wait for, and
 * the caller should fall through to its normal (non-countdown) rendering.
 */
export const hasCountdownElapsed = (
  targetDate: string | number | Date | null | undefined,
  now: number
): boolean => {
  if (!targetDate) {
    return true
  }

  const target = new Date(targetDate).getTime()

  if (Number.isNaN(target)) {
    return true
  }

  return target - now <= 0
}
